import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EventNotFound from "@/components/EventNotFound/EventNotFound";
import Loader from "@/components/Loader/Loader";
import Hey from "@/components/SVGComponents/Hey";
import {
  BellNavIcon,
  CalendarMiniIcon,
  PinMiniIcon,
} from "@/components/SVGComponents/MemberIcons";
import { useNotificationCenter } from "@/contexts/NotificationContext";
import { getCurrentUser } from "@/lib/utils";
import styles from "./Events.module.scss";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchAvailableEvents } from "@/store/slices/eventSlice";
import type { Event as DinnerEvent } from "@/services/api/events";
import EventDetailsModal from "./components/EventDetailsModal/EventDetailsModal";

type DinnerCard = {
  id: number | string;
  title: string;
  date: string;
  time: string;
  guests: string;
  guestCount: number;
  location: string | null;
  mapUrl: string | null;
  description: string;
  isEventAttended: boolean;
};

const formatEventDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const formatEventTime = (timeString: string) => {
  const time = new Date(timeString);

  if (Number.isNaN(time.getTime())) {
    return "";
  }

  return time.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const Event = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    notificationCount,
    isNotificationOpen,
    openNotifications,
  } = useNotificationCenter();
  const { events, isLoading, error } = useAppSelector((state) => state.events);
  const [selectedEvent, setSelectedEvent] = useState<DinnerCard | null>(null);

  const userName =
    getCurrentUser("name") ||
    getCurrentUser("userName") ||
    getCurrentUser("email")?.split("@")?.[0]?.replace(/\./g, " ") ||
    "John";

  const loadDinners = () => {
    dispatch(
      fetchAvailableEvents({
        page: 0,
        size: 10,
        isEventApplied: true,
      }),
    );
  };

  useEffect(() => {
    loadDinners();
  }, [dispatch]);

  const safeEvents = events || [];
  const { upcomingDinners, attendedDinners } = useMemo(() => {
    const mappedEvents = safeEvents.map((dinner: DinnerEvent): DinnerCard => ({
      id: dinner.id,
      title: dinner.name,
      date: formatEventDate(dinner.eventDate),
      time: formatEventTime(dinner.eventStartTime),
      guests: `${dinner.capacity > 0 ? dinner.capacity : 0} Guests`,
      guestCount: dinner.capacity,
      location: dinner.location,
      mapUrl: dinner.mapUrl,
      description: dinner.description || "",
      isEventAttended: Boolean(dinner.isEventAttended),
    }));

    return {
      upcomingDinners: mappedEvents.filter((dinner) => !dinner.isEventAttended),
      attendedDinners: mappedEvents.filter((dinner) => dinner.isEventAttended),
    };
  }, [safeEvents]);

  const hasDinners = upcomingDinners.length > 0 || attendedDinners.length > 0;

  const handleOpenEventDetails = (dinner: DinnerCard) => {
    setSelectedEvent(dinner);
  };

  const handleCloseEventDetails = () => {
    setSelectedEvent(null);
  };

  const emptyTitle = error ? "We couldn't load your dinners" : "No dinners scheduled yet";
  const emptyDescription = error
    ? "Please refresh the list. Once your dinner schedule is available, it will show up here."
    : "Your next meaningful conversation starts with a selection.";

  const renderDinnerCard = (dinner: DinnerCard) => {
    const isAttended = dinner.isEventAttended;
    const detailLine = [dinner.date, dinner.time, dinner.guests]
      .filter(Boolean)
      .join(" • ");
    const locationText = dinner.location || dinner.description;
    const showPin = Boolean(dinner.location);

    return (
      <button
        aria-label={`View details for ${dinner.title}`}
        className={`${styles.eventCard} ${isAttended ? styles.eventCardAttended : ""}`}
        key={dinner.id}
        onClick={() => handleOpenEventDetails(dinner)}
        type="button"
      >
        <div className={styles.eventMain}>
          <h3 className={styles.eventTitle}>{dinner.title}</h3>

          <p className={styles.metaLine}>{detailLine}</p>

          {locationText ? (
            <div className={styles.locationRow}>
              {showPin ? <PinMiniIcon className={styles.pinIcon} /> : null}
              <span>{locationText}</span>
            </div>
          ) : null}
        </div>

        {isAttended ? (
          <div className={styles.attendedBadge}>Attended</div>
        ) : (<></>
          // <div aria-hidden className={styles.calendarButton}>
          //   <CalendarMiniIcon />
          // </div>
        )}
      </button>
    );
  };

  return (
    <>
      <section className={styles.page}>
        <div className={styles.orbLeft} />
        <div className={styles.orbRight} />

        <header className={styles.header}>
          <div className={styles.desktopHeader}>
            <h1 className={styles.title}>
              Welcome back, <span>{userName}</span>
              <Hey />
            </h1>
          </div>

          <div className={styles.mobileHeaderRow}>
            <h1 className={styles.mobileTitle}>
              Welcome back, <span>{userName}</span>
            </h1>

            <button
              aria-label="Notifications"
              aria-expanded={isNotificationOpen}
              aria-haspopup="dialog"
              className={styles.mobileNotificationButton}
              onClick={openNotifications}
              type="button"
            >
              <BellNavIcon />
              {notificationCount > 0 ? (
                <span className={styles.mobileNotificationDot} />
              ) : null}
            </button>
          </div>
        </header>

        {isLoading ? (
          <Loader className={styles.statePanel} fullscreen={false} size={92} />
        ) : hasDinners ? (
          <div className={styles.sections}>
            {upcomingDinners.length > 0 ? (
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.subtitle}>Your Dinners</h2>
                  <p className={styles.copy}>
                    Track your upcoming tables and stay updated on your dinner
                    details.
                  </p>
                </div>

                <div className={styles.list}>
                  {upcomingDinners.map(renderDinnerCard)}
                </div>
              </section>
            ) : null}

            {attendedDinners.length > 0 ? (
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.subtitle}>Attended Dinners</h2>
                </div>

                <div className={styles.list}>
                  {attendedDinners.map(renderDinnerCard)}
                </div>
              </section>
            ) : null}
          </div>
        ) : (
          <EventNotFound
            actionLabel={error ? "Refresh Dinners" : "Explore Dinners"}
            className={styles.statePanel}
            description={emptyDescription}
            onAction={error ? loadDinners : () => navigate("/home")}
            title={emptyTitle}
          />
        )}
      </section>

      <EventDetailsModal
        event={
          selectedEvent
            ? {
                id: selectedEvent.id,
                title: selectedEvent.title,
                description: selectedEvent.description,
                date: selectedEvent.date,
                time: selectedEvent.time,
                guestCount: selectedEvent.guestCount,
                location: selectedEvent.location,
                mapUrl: selectedEvent.mapUrl,
              }
            : null
        }
        isOpen={Boolean(selectedEvent)}
        onClose={handleCloseEventDetails}
      />
    </>
  );
};

export default Event;
